import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Text,
  Button,
  Card,
  CardBody,
  Badge,
  SimpleGrid,
  Spinner,
  Alert,
  AlertIcon,
  useToast,
  IconButton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  useBreakpointValue,
  useDisclosure,
  Divider,
  Image,
} from '@chakra-ui/react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { ArrowBackIcon, HamburgerIcon, CloseIcon } from '@chakra-ui/icons';
import { supabase } from '../supabaseClient';

const TestDetail = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [test, setTest] = useState(null);
  const [associations, setAssociations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isTextExpanded, setIsTextExpanded] = useState(false);
  
  // Mobile menu state
  const { isOpen: isMobileMenuOpen, onOpen: onMobileMenuOpen, onClose: onMobileMenuClose } = useDisclosure();
  const isMobile = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    if (testId) {
      fetchTestDetail();
    }
  }, [testId]);

  const fetchTestDetail = async () => {
    try {
      setLoading(true);
      
      // First try to fetch from tests table
      let { data: testData, error: testError } = await supabase
        .from('tests')
        .select('*')
        .eq('id', testId)
        .single();

      let isPanel = false;
      
      // If not found in tests, try panels table
      if (!testData) {
        const { data: panelData, error: panelError } = await supabase
          .from('panels')
          .select('*')
          .eq('id', testId)
          .single();
        
        if (panelData) {
          testData = { ...panelData, type: 'panel' };
          isPanel = true;
        } else {
          console.error('Test not found in either table');
          return;
        }
      } else {
        testData.type = 'test';
      }

      setTest(testData);

      // Fetch associations
      if (isPanel) {
        const { data: associationData } = await supabase
          .from('panel_tests')
          .select(`
            test_id,
            tests (*)
          `)
          .eq('panel_id', testId);
        
        setAssociations(associationData?.map(a => a.tests) || []);
      } else {
        const { data: associationData } = await supabase
          .from('panel_tests')
          .select(`
            panel_id,
            panels (*)
          `)
          .eq('test_id', testId);
        
        setAssociations(associationData?.map(a => a.panels) || []);
      }

    } catch (error) {
      console.error('Error fetching test detail:', error);
      toast({
        title: 'Error loading test details',
        description: 'Please try again later',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box minH="100vh" bg="gray.50">
        <Container maxW="6xl" py={8}>
          <VStack justify="center" align="center" minH="50vh" spacing={4}>
            <Spinner size="xl" color="blue.500" />
            <Text>Loading test details...</Text>
          </VStack>
        </Container>
      </Box>
    );
  }

  if (!test) {
    return (
      <Box minH="100vh" bg="gray.50">
        <Container maxW="6xl" py={8}>
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            Test not found. Please check the URL or go back to the test directory.
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Header with Navigation */}
      <Box 
        as="header" 
        bg="white" 
        boxShadow="sm" 
        position="sticky" 
        top={0} 
        zIndex={10}
        px={{ base: 4, md: 12 }} 
        py={3}
      >
        <HStack justify="space-between" align="center">
          <Image src="/logo.png" alt="At Home Blood Draw Logo" maxW="320px" w="100%" />
          
          {isMobile ? (
            <>
              <IconButton 
                icon={<HamburgerIcon />} 
                variant="ghost" 
                onClick={onMobileMenuOpen} 
                aria-label="Open menu" 
                fontSize="2xl" 
              />
              <Drawer isOpen={isMobileMenuOpen} placement="right" onClose={onMobileMenuClose}>
                <DrawerOverlay />
                <DrawerContent>
                  <DrawerHeader display="flex" alignItems="center" justifyContent="space-between">
                    <Box>Menu</Box>
                    <IconButton 
                      icon={<CloseIcon />} 
                      onClick={onMobileMenuClose} 
                      aria-label="Close menu" 
                    />
                  </DrawerHeader>
                  <DrawerBody>
                    <VStack spacing={4} align="stretch" mt={4}>
                      <Button as={RouterLink} to="/test-directory" colorScheme="green" variant="solid" size="lg">
                        Test Directory
                      </Button>
                      <Button as={RouterLink} to="/lab-directory" colorScheme="purple" variant="ghost" size="lg">
                        Lab Directory
                      </Button>
                      <Button as={RouterLink} to="/doctor/login" colorScheme="blue" variant="ghost" size="lg">
                        Physician Login
                      </Button>
                      <Button as={RouterLink} to="/patient/login" colorScheme="blue" variant="ghost" size="lg">
                        Patient Login
                      </Button>
                    </VStack>
                  </DrawerBody>
                </DrawerContent>
              </Drawer>
            </>
          ) : (
            <HStack spacing={4}>
              <Button as={RouterLink} to="/test-directory" colorScheme="green" variant="solid">
                Test Directory
              </Button>
              <Button as={RouterLink} to="/lab-directory" colorScheme="purple" variant="ghost">
                Lab Directory
              </Button>
              <Button as={RouterLink} to="/doctor/login" colorScheme="blue" variant="ghost">
                Physician Login
              </Button>
              <Button as={RouterLink} to="/patient/login" colorScheme="blue" variant="ghost">
                Patient Login
              </Button>
            </HStack>
          )}
        </HStack>
      </Box>

      <Container maxW="6xl" py={8}>
        {/* Back Button */}
        <Button 
          leftIcon={<ArrowBackIcon />} 
          variant="ghost" 
          mb={6}
          onClick={() => navigate('/test-directory')}
        >
          Back to Test Directory
        </Button>

        {/* Main Layout */}
        <SimpleGrid columns={{ base: 1, lg: 5 }} spacing={8}>
          {/* Left Content - Test Information */}
          <Box gridColumn={{ base: '1', lg: '1 / 4' }}>
            <VStack spacing={8} align="stretch">
              
              {/* Test Header */}
              <Card>
                <CardBody p={8}>
                  <VStack align="start" spacing={4}>
                    <HStack spacing={3} align="center">
                      <Heading size="2xl" color="blue.600">{test.name}</Heading>
                      <Badge colorScheme={test.type === 'panel' ? 'purple' : 'blue'} fontSize="md" px={3} py={1}>
                        {test.type === 'panel' ? 'Panel' : 'Test'}
                      </Badge>
                    </HStack>
                    
                    {test.description && (
                      <Text fontSize="lg" color="gray.600" lineHeight="1.8">
                        {test.description}
                      </Text>
                    )}
                    
                    {/* Key Information Row */}
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} w="full" pt={4}>
                      {test.fasting_required && (
                        <VStack spacing={1} align="start">
                          <Text fontSize="xs" color="orange.500" fontWeight="bold">FASTING</Text>
                          <Text fontSize="sm">Required</Text>
                        </VStack>
                      )}
                      {test.cash_price && (
                        <VStack spacing={1} align="start">
                          <Text fontSize="xs" color="green.500" fontWeight="bold">PRICE</Text>
                          <Text fontSize="sm" fontWeight="bold">${test.cash_price}</Text>
                        </VStack>
                      )}
                      {test.turnaround_time && (
                        <VStack spacing={1} align="start">
                          <Text fontSize="xs" color="blue.500" fontWeight="bold">TAT</Text>
                          <Text fontSize="sm">{test.turnaround_time}</Text>
                        </VStack>
                      )}
                      {test.preferred_specimen && (
                        <VStack spacing={1} align="start">
                          <Text fontSize="xs" color="purple.500" fontWeight="bold">SPECIMEN</Text>
                          <Text fontSize="sm">{test.preferred_specimen}</Text>
                        </VStack>
                      )}
                    </SimpleGrid>
                  </VStack>
                </CardBody>
              </Card>

              {/* Clinical Information */}
              <Card>
                <CardBody p={6}>
                  <Heading size="lg" mb={4} color="blue.600">Clinical Information</Heading>
                  {test.clinical_significance ? (
                    <VStack align="start" spacing={3}>
                      <Text lineHeight="1.8" color="gray.700">
                        {isTextExpanded || test.clinical_significance.length <= 300
                          ? test.clinical_significance
                          : `${test.clinical_significance.substring(0, 300)}...`
                        }
                      </Text>
                      {test.clinical_significance.length > 300 && (
                        <Button
                          variant="link"
                          colorScheme="blue"
                          fontSize="sm"
                          fontWeight="medium"
                          onClick={() => setIsTextExpanded(!isTextExpanded)}
                          p={0}
                        >
                          {isTextExpanded ? 'See less' : 'See more'}
                        </Button>
                      )}
                    </VStack>
                  ) : (
                    <Text color="gray.500" fontStyle="italic">
                      Clinical significance information not available for this test.
                    </Text>
                  )}
                </CardBody>
              </Card>

              {/* Test Details */}
              <Card>
                <CardBody p={6}>
                  <Heading size="lg" mb={6} color="blue.600">Test Details</Heading>
                  
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                    {/* Specimen Information */}
                    <Box>
                      <Heading size="md" mb={4} color="gray.700">Specimen Requirements</Heading>
                      <VStack align="start" spacing={3}>
                        <HStack>
                          <Text fontWeight="medium" minW="120px" color="gray.600">Preferred:</Text>
                          <Text>{test.preferred_specimen || 'Not specified'}</Text>
                        </HStack>
                        {test.alternative_specimen && (
                          <HStack>
                            <Text fontWeight="medium" minW="120px" color="gray.600">Alternative:</Text>
                            <Text>{test.alternative_specimen}</Text>
                          </HStack>
                        )}
                        <HStack>
                          <Text fontWeight="medium" minW="120px" color="gray.600">Min Volume:</Text>
                          <Text>{test.minimum_volume || 'Standard volume'}</Text>
                        </HStack>
                        <HStack>
                          <Text fontWeight="medium" minW="120px" color="gray.600">Tube:</Text>
                          <Text>{test.tube_top_color || 'Standard tube'}</Text>
                        </HStack>
                      </VStack>
                    </Box>

                    {/* Processing Information */}
                    <Box>
                      <Heading size="md" mb={4} color="gray.700">Processing Information</Heading>
                      <VStack align="start" spacing={3}>
                        <HStack>
                          <Text fontWeight="medium" minW="120px" color="gray.600">Method:</Text>
                          <Text>{test.methodology || 'Standard methodology'}</Text>
                        </HStack>
                        <HStack>
                          <Text fontWeight="medium" minW="120px" color="gray.600">TAT:</Text>
                          <Text>{test.turnaround_time || '1-2 business days'}</Text>
                        </HStack>
                        {test.reference_range && (
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="medium" color="gray.600">Reference Range:</Text>
                            <Text fontSize="sm" color="gray.600" pl={2}>
                              {test.reference_range}
                            </Text>
                          </VStack>
                        )}
                      </VStack>
                    </Box>
                  </SimpleGrid>

                  {/* Additional Information */}
                  {(test.patient_preparation || test.interfering_factors) && (
                    <>
                      <Divider my={6} />
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                        {test.patient_preparation && (
                          <Box>
                            <Heading size="md" mb={3} color="blue.600">Patient Preparation</Heading>
                            <Text fontSize="sm" lineHeight="1.6" color="gray.700">
                              {test.patient_preparation}
                            </Text>
                          </Box>
                        )}
                        {test.interfering_factors && (
                          <Box>
                            <Heading size="md" mb={3} color="orange.600">Interfering Factors</Heading>
                            <Text fontSize="sm" lineHeight="1.6" color="gray.700">
                              {test.interfering_factors}
                            </Text>
                          </Box>
                        )}
                      </SimpleGrid>
                    </>
                  )}
                </CardBody>
              </Card>

              {/* Related Tests/Panels */}
              {associations.length > 0 && (
                <Card>
                  <CardBody p={6}>
                    <Heading size="lg" mb={4} color="blue.600">
                      {test.type === 'panel' ? 'Tests in this Panel' : 'Related Panels'}
                    </Heading>
                    <VStack align="start" spacing={3}>
                      {associations.map((item) => (
                        <Box
                          key={item.id}
                          w="full"
                          p={4}
                          border="1px solid"
                          borderColor="gray.200"
                          borderRadius="md"
                          cursor="pointer"
                          _hover={{ bg: "gray.50", borderColor: "blue.200" }}
                          onClick={() => navigate(`/test-directory/${item.id}`)}
                        >
                          <HStack justify="space-between">
                            <VStack align="start" spacing={1}>
                              <Text fontWeight="medium" color="blue.600">{item.name}</Text>
                              {item.description && (
                                <Text fontSize="sm" color="gray.600">{item.description}</Text>
                              )}
                            </VStack>
                            {item.cash_price && (
                              <Badge colorScheme="blue" variant="subtle">
                                ${item.cash_price}
                              </Badge>
                            )}
                          </HStack>
                        </Box>
                      ))}
                    </VStack>
                  </CardBody>
                </Card>
              )}
            </VStack>
          </Box>

          {/* Right Sidebar - Booking CTA */}
          <Box gridColumn={{ base: '1', lg: '4 / 6' }}>
            <Box position="sticky" top="100px">
              <Card bg="blue.50" border="2px solid" borderColor="blue.200" boxShadow="lg">
                <CardBody p={6}>
                  <Heading size="lg" mb={6} color="blue.600" textAlign="center">Book This Test</Heading>
                  <VStack spacing={4} align="stretch">
                    {/* Home Visit Option */}
                    <Card bg="green.50" border="2px solid" borderColor="green.200">
                      <CardBody p={4}>
                        <VStack spacing={3} align="center">
                          <Text fontSize="2xl">🏠</Text>
                          <Heading size="md" color="green.700">Home Visit</Heading>
                          <Text fontSize="sm" color="green.600" textAlign="center">
                            Convenient at-home collection
                          </Text>
                          <Button colorScheme="green" size="sm" width="full">
                            Schedule Home Visit
                          </Button>
                        </VStack>
                      </CardBody>
                    </Card>
                    
                    {/* Lab Location Option */}
                    <Card bg="blue.50" border="2px solid" borderColor="blue.200">
                      <CardBody p={4}>
                        <VStack spacing={3} align="center">
                          <Text fontSize="2xl">🏥</Text>
                          <Heading size="md" color="blue.700">Lab Location</Heading>
                          <Text fontSize="sm" color="blue.600" textAlign="center">
                            Visit our partner labs
                          </Text>
                          <Button colorScheme="blue" size="sm" width="full">
                            Find Lab Near Me
                          </Button>
                        </VStack>
                      </CardBody>
                    </Card>
                    
                    {/* Doctor Referral Option */}
                    <Card bg="purple.50" border="2px solid" borderColor="purple.200">
                      <CardBody p={4}>
                        <VStack spacing={3} align="center">
                          <Text fontSize="2xl">👨‍⚕️</Text>
                          <Heading size="md" color="purple.700">Doctor Referral</Heading>
                          <Text fontSize="sm" color="purple.600" textAlign="center">
                            Get referred by your doctor
                          </Text>
                          <Button colorScheme="purple" size="sm" width="full">
                            Get Referral
                          </Button>
                        </VStack>
                      </CardBody>
                    </Card>
                    
                    <Divider my={4} />
                    
                    {/* Contact Information */}
                    <VStack spacing={3} align="stretch">
                      <Heading size="sm" color="blue.600" textAlign="center">Need Help?</Heading>
                      <VStack spacing={2} fontSize="sm">
                        <HStack justify="center">
                          <Text fontWeight="medium">📞 Call:</Text>
                          <Text color="blue.600">+1 844-718-7800</Text>
                        </HStack>
                        <HStack justify="center">
                          <Text fontWeight="medium">✉️ Email:</Text>
                          <Text color="blue.600">contact@athomeblooddraw.com</Text>
                        </HStack>
                      </VStack>
                    </VStack>
                  </VStack>
                </CardBody>
              </Card>
            </Box>
          </Box>
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default TestDetail; 